/**
 * Submitter-facing action panel — shown on the right rail of the ticket
 * detail page when the submitter (not an admin) is viewing their own ticket.
 *
 *  - "This isn't fixed yet" → moves Ready for Testing back to Needs Rework.
 *    Requires a short note so the admin/AI knows what's still wrong; the note
 *    is stored as a regular comment so the back-and-forth lives in one thread.
 *  - "Confirm this is fixed" → moves the ticket to Confirmed Fixed and locks
 *    it from any further submitter edits.
 *
 * Both transitions go through `updateMyTicketStatus` on the backend, which
 * enforces the allowed-transition whitelist.
 */
import { useCallback, useState } from 'react';
import {
  Box, Button, Dialog, DialogActions, DialogContent, DialogContentText,
  DialogTitle, Paper, TextField, Typography,
} from '@mui/material';
import { CheckCircleOutline, ReplayOutlined } from '@mui/icons-material';
import { alpha, useTheme } from '@mui/material/styles';
import { updateMyTicketStatus } from '../../services/feedbackService';
import type { FeedbackTicketDetail } from '../../types/feedback';

interface Props {
  ticket: FeedbackTicketDetail;
  onChanged: () => void;
}

export const SubmitterActions = ({ ticket, onChanged }: Props) => {
  const theme = useTheme();
  const [rejectOpen, setRejectOpen] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [note, setNote] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  // Reject is only meaningful while a solution is actually on the table.
  const canReject = ticket.Status === 'Ready for Testing';
  // Confirm is allowed after either Ready for Testing OR Fixed (admin marked
  // it fixed without going through the testing round — submitter still gets
  // the final say).
  const canConfirm = ticket.Status === 'Ready for Testing' || ticket.Status === 'Fixed';

  if (!canReject && !canConfirm) return null;

  const handleReject = useCallback(async () => {
    const trimmed = note.trim();
    if (!trimmed) {
      setError('Please describe what is still wrong.');
      return;
    }
    setBusy(true);
    setError('');
    try {
      await updateMyTicketStatus(ticket.FeedbackTicketKey, 'Needs Rework', trimmed);
      setRejectOpen(false);
      setNote('');
      onChanged();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to send back for rework');
    } finally {
      setBusy(false);
    }
  }, [note, ticket.FeedbackTicketKey, onChanged]);

  const handleConfirm = useCallback(async () => {
    setBusy(true);
    setError('');
    try {
      await updateMyTicketStatus(
        ticket.FeedbackTicketKey,
        'Confirmed Fixed',
        note.trim() || undefined,
      );
      setConfirmOpen(false);
      setNote('');
      onChanged();
    } catch (err: any) {
      setError(err?.response?.data?.error || err?.message || 'Failed to confirm fix');
    } finally {
      setBusy(false);
    }
  }, [note, ticket.FeedbackTicketKey, onChanged]);

  return (
    <Paper variant="outlined" sx={{
      p: 2, borderRadius: 1.5, mb: 2,
      borderLeft: `4px solid ${theme.palette.success.main}`,
      bgcolor: alpha(theme.palette.success.main, 0.03),
    }}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1.5, fontSize: '0.85rem', color: 'success.main' }}>
        Your decision
      </Typography>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25, lineHeight: 1.4 }}>
        {canReject
          ? 'A solution has been proposed. Confirm it works, or send it back for rework.'
          : 'The admin marked this fixed. Confirm it works on your end to close it out.'}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
        <Button
          variant="contained" color="success" size="small" startIcon={<CheckCircleOutline />}
          disabled={!canConfirm} onClick={() => setConfirmOpen(true)}
          sx={{ textTransform: 'none', fontWeight: 600 }}>
          Confirm this is fixed
        </Button>
        {canReject && (
          <Button
            variant="outlined" color="warning" size="small" startIcon={<ReplayOutlined />}
            onClick={() => setRejectOpen(true)}
            sx={{ textTransform: 'none', fontWeight: 600 }}>
            Send back for rework
          </Button>
        )}
      </Box>

      <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 1.25, fontSize: '0.68rem' }}>
        Confirming locks this ticket — you won't be able to add comments or attachments afterward.
      </Typography>

      {/* Reject dialog — note is required */}
      <Dialog open={rejectOpen} onClose={() => !busy && setRejectOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 0.5 }}>Send back for rework</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1.5, fontSize: '0.85rem' }}>
            Tell the admin/AI what's still wrong so the next attempt has the right context.
            Your note will be posted as a comment in the thread.
          </DialogContentText>
          <TextField
            autoFocus
            label="What's still wrong?"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            fullWidth multiline minRows={3} maxRows={10}
            inputProps={{ maxLength: 10_000 }}
          />
          {error && <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>{error}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRejectOpen(false)} disabled={busy} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleReject} disabled={busy || !note.trim()}
            variant="contained" color="warning" sx={{ textTransform: 'none', fontWeight: 600 }}>
            {busy ? 'Sending…' : 'Send back'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Confirm dialog — note is optional */}
      <Dialog open={confirmOpen} onClose={() => !busy && setConfirmOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ pb: 0.5 }}>Confirm this is fixed</DialogTitle>
        <DialogContent>
          <DialogContentText sx={{ mb: 1.5, fontSize: '0.85rem' }}>
            Confirming will lock this ticket. You won't be able to edit it, add attachments,
            or post further comments. Are you sure?
          </DialogContentText>
          <TextField
            label="Note (optional)"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            fullWidth multiline minRows={2} maxRows={6}
            placeholder="e.g. Tested and looks good, thanks."
            inputProps={{ maxLength: 10_000 }}
          />
          {error && <Typography variant="caption" color="error" sx={{ mt: 1, display: 'block' }}>{error}</Typography>}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmOpen(false)} disabled={busy} sx={{ textTransform: 'none' }}>Cancel</Button>
          <Button onClick={handleConfirm} disabled={busy}
            variant="contained" color="success" sx={{ textTransform: 'none', fontWeight: 600 }}>
            {busy ? 'Confirming…' : 'Yes, confirm and lock'}
          </Button>
        </DialogActions>
      </Dialog>
    </Paper>
  );
};
